import themeTokens from '@/config/theme-tokens.js';
import type {
  DrawerName,
  GeoLocationRange,
  WaterLevelStatus,
  WaterLevelThreshold,
  RainfallStatusName,
  RainfallStatus,
  RainfallThreshold,
  MapType,
  SpotPopupType,
  ZoomLevelThresholds,
  CustomComponents,
  RemainingWaterName,
  WaterSupplyImagesName,
  GeofencePosition,
  UpdateDateType,
  ObservationScrapingImagesName,
  UpdateStatusType,
  GeojsonCustomPropertyName,
} from '@/types';

const { color: themeColor } = themeTokens;

export const queryParameters = {
  SORT: 'sort',
  GROUP_FLAGS: 'groupFlags',
  ENABLE_LAYERS: 'enableLayers',
  LIST_SEARCH: 'listSearch',
  CODE: 'code',
};

// 雨量観測
export const rainfallStatusName: RainfallStatusName = {
  NOTOBSERVED: '観測雨量なし',
  NORMAL: '普通の雨',
  SLIGHTLY: 'やや強い雨',
  HEAVY: '強い雨',
  INTENSE: '激しい雨',
  VERYHEAVY: '非常に激しい雨',
  SEVERE: '猛烈な雨',
};

export const rainfallStatus: RainfallStatus = {
  NOTOBSERVED: 'NotObserved',
  NORMAL: 'Normal',
  SLIGHTLY: 'Slightly',
  HEAVY: 'Heavy',
  INTENSE: 'Intense',
  VERYHEAVY: 'VeryHeavy',
  SEVERE: 'Severe',
};

export const rainfallThreshold: RainfallThreshold = {
  SLIGHTLY_MIN: 1.7,
  HEAVY_MIN: 3.3,
  INTENSE_MIN: 5,
  VERYHEAVY_MIN: 8.3,
  SEVERE_MIN: 13.3,
};

// 水位観測
export const waterLevelThreshold: WaterLevelThreshold = {
  LOWERLIMITOFAGRAPH: 'lowerLimitOfAGraph',
  THRESHOLDREPORT: 'thresholdReport',
  THRESHOLDVIGILANCE: 'thresholdVigilance',
  THRESHOLDSPECIALVIGILANCE: 'thresholdSpecialVigilance',
  THRESHOLDSPECIALDANGER: 'thresholdSpecialDanger',
};

export const waterLevelStatusName = {
  NOTIFICATION: '通報水位',
  ALERT: '警戒水位',
  SPECIAL_ALERT: '特別警戒水位',
  USUAL: '平常水位',
  DANGEROUS: '危険水位',
  OBSERVATION: '観測水位',
};

export const waterLevelStatus: WaterLevelStatus = {
  NOTIFICATION: 'Notification',
  ALERT: 'Alert',
  SPECIAL_ALERT: 'SpecialAlert',
  USUAL: 'Usual',
  DANGEROUS: 'Dangerous',
  OBSERVATION: 'Observation',
};

export const customComponents: CustomComponents = {
  WATERLEVELGRAPH: 'waterLevelGraph',
  RAINFALLGRAPH: 'rainfallGraph',
};

export const classification = {
  SPOT: 'spot',
  LINK: 'link',
  OVERLAY: 'overlay',
  CLOSEUP: 'closeUp',
  BANNER: 'banner',
  INFORMATION: 'information',
  ADMIN_SIGNED_IN_INFORMATION: 'adminSignedInInformation',
  HELP: 'help',
};

export const subClass = {
  DEFAULT: '',
  POST: 'post',
  WATER_SUPPLY: 'waterSupply',
  ICON_WITH_STATUS: 'iconWithStatus',
};

export const mapType: MapType = {
  HAZARDMAP: 'hazardMap',
  SAFETYMAP: 'safetyMap',
  SIGHTSEEINGMAP: 'sightseeingMap',
  ALL: 'all',
};

// スポットポップアップ
export const spotPopupType: SpotPopupType = {
  FIXED_ICON: 'fixedIcon',
  FLEXIBLE_ICON: 'flexibleIcon',
  FIXED_ICON_WITH_NAME: 'fixedIconWithName',
  FIXED_ICON_WITH_STATUS: 'fixedIconWithStatus',
  FIXED_ICON_WITH_NAME_AND_STATUS: 'fixedIconWithNameAndStatus',
};

export const zoomLevelThresholds: ZoomLevelThresholds = {
  SHOW_SPOT_NAME: 16,
};

export const geolocationRange: GeoLocationRange = {
  MAX_LATITUDE: 90,
  MIN_LATITUDE: -90,
  MAX_LONGITUDE: 180,
  MIN_LONGITUDE: -180,
};

export const classNames = {
  MARKER: {
    USER_POSITION: 'ol-user-position-marker',
    SPOT_MARKER: 'ol-spot-marker',
    COMMUNITY_BUS: 'ol-spot-marker_community_bus_shelter',
    RAINFALL_LEVEL_OBSERVATORY: 'ol-spot-marker_rainfall',
    SHELTER: 'ol-spot-marker_shelter',
    SELF_DRIVING_BUS: 'ol-spot-marker_self-driving-bus_shelter',
    WATER_LEVEL_OBSERVATORY: 'ol-spot-marker_waterlevel',
  },
  PINS: {
    PIN: 'ol-spot-pin',
    COMMUNITY_BUS: 'ol-spot-pin_community_bus_shelter',
    SHELTER: 'ol-spot-pin_shelter',
    RAINFALL_LEVEL_OBSERVATORY: 'ol-spot-pin_rainfall',
    SELF_DRIVING_BUS: 'ol-spot-pin_self_driving_bus',
    WATER_LEVEL_OBSERVATORY: 'ol-spot-pin_waterlevel',
  },
  VISIBLE: {
    BLOCK: 'is-block',
    HIDDEN: 'is-none',
  },
};

export const drawerName: DrawerName = {
  SPOTS: 'spots',
  HOME: 'home',
  LINKS: 'links',
  HELP: 'help',
  FAVORITES: 'favorites',
  POST: 'post',
};

export const favoriteKind = {
  SPOT: 'spot',
  LAYER: 'layer',
  LINK: 'link',
  HELP: 'help',
  POINT: 'point',
} as const;

export const helpElementKey = {
  SANO: 'sano',
  TOYONAKA: 'toyonaka',
  WATER_SUPPLY_PORTAL_TRIAL: 'water-supply-portal-trial',
  OTARI: 'otari',
  UHURU_DEMO: 'uhuru-demo',
} as const;

export const helpLinkPath = {
  USAGE_GUIDE: 'usage-guide',
  TERMS_OF_USE: 'terms-of-use',
  DATA_SOURCE: 'data-source',
  BUTTON_GUIDE: 'button-guide',
} as const;

export const spotGroupingType = {
  SPOTTYPE: 'spotType',
  TAG: 'tag',
};

export const spotGroupingTagCondition = {
  OR: 'OR',
  AND: 'AND',
};

export const spotGroupingTagDefaultSelection = {
  ALL: 'ALL',
  LEFT_ONE: 'LEFT_ONE',
};

export const spotState = {
  ENABLE: 'enable',
  DISABLE: 'disable',
};

export const color = {
  activeColor: themeColor.brand.primary,
  inactiveColor: themeColor.gray.inactive,
  buttonGray: themeColor.gray.button,
  listColor: themeColor.hazardMap.list,
  tagColor: '#f2613d',
  customLightGray: themeColor.gray.surface,
  customDarkGray: themeColor.gray.muted,
  customPaleBlue: themeColor.misc.customPaleBlue,
  rainFallGraph: {
    lightBlue: '#16BADB',
    vividBlue: '#1689DB',
    vividYellow: '#FAD532',
    vividOrange: '#FA7532',
    vividRed: '#F03043',
    vividPurple: '#8F52CC',
    vividGreen: '#29CC5F',
  },
  waterLevelGraph: {
    lightBlue: '#16BADB',
  },
  taiji: {
    signage: {
      customDarkRed: '#C10605',
    },
  },
  layerTagColor: '#EB6A0C',
  postIconColor: '#f2613d',
  movementTracksButton: {
    enable: '#91B122',
    disable: '#FFFFFF',
  },
};

// 給水ポータル機能
export const congestionLevelLabel = {
  uncongested: '余裕あり',
  bitCongested: 'やや混雑',
  congested: '混雑',
};

export const remainingWaterLabel = {
  full: '十分',
  few: '残り僅か',
  empty: 'なし',
};

export const nextTimeLabel = {
  withinAnHour: '1時間以内',
  withinThreeHour: '3時間以内',
  pending: '未定',
};

export const congestionLevelName = {
  UNCONGESTED: 'uncongested',
  BITCONGESTED: 'bitCongested',
  CONGESTED: 'congested',
};

export const remainingWaterName: RemainingWaterName = {
  FULL: 'full',
  FEW: 'few',
  EMPTY: 'empty',
};

export const waterSupplyImagesName: WaterSupplyImagesName = {
  FULL: 'full',
  MANY: 'many',
  FEW: 'few',
  EMPTY: 'empty',
};

export const observationScrapingImagesName: ObservationScrapingImagesName = {
  NORMAL: 'normal',
  EMERGENCY: 'emergency',
};

export const geofencePosition: GeofencePosition = {
  CENTER: 'center',
  RIGHT: 'right',
  LEFT: 'left',
  END: 'end',
};

// お知らせ機能
export const informationColorTypeName = {
  DISASTER: 'disaster',
  BLUE: 'blue',
  GREEN: 'green',
};

export const informationPeriodTypeName = {
  ALWAYS: 'always',
  DOW: 'dow',
  RANGE: 'range',
};

export const informationImportanceTypeName = {
  LOW: 'low',
  MIDDLE: 'middle',
  HIGH: 'high',
};

export const informationColor = {
  middle: { titleColor: '#F03043', backGroundColor: '#E8E8E8' },
  disaster: { titleColor: '#F8F01B', backGroundColor: '#F03043' },
  blue: { titleColor: '#334655', backGroundColor: '#E8E8E8' },
  green: { titleColor: '#91B122', backGroundColor: '#E8E8E8' },
};

export const updateDateType: UpdateDateType = {
  GPS_LOGGER: 'gpsLogger',
  OBSERVATION_SCRAPING: 'observationScraping',
  SPOT_AND_PROPERTIES: 'spotAndProperties',
};

export const updateStatusType: UpdateStatusType = {
  IDLE: 'idle',
  UPDATING: 'updating',
  UPDATED: 'updated',
};

export const overlayButtonType = {
  LIST: 'list',
  TEXT: 'text',
  TAG: 'tag',
  HIDDEN: 'hidden',
};

export const layerIdSuffix = {
  LINE: '-line',
  POINT: '-point',
};

export const layerType = {
  CIRCLE: 'circle',
};

export const authenticationMethodType = {
  API_KEY: 'apiKey',
  TOKEN: 'token',
  API_KEY_AND_TOKEN: 'apiKeyAndToken',
  ADMIN_TOKEN: 'adminToken',
};

export const layerSourceType = {
  CUCON: 'cucon',
  MAPBOX: 'mapbox',
  DISAPORTALDATA: 'disaportaldata',
};

export const markerSizePattern = {
  SMALL: 'small',
  MIDDLE: 'middle',
  LARGE: 'large',
};

export const ASSET_ATTRIBUTES_SCHEMA_CONTENTS_TYPE = {
  HIDDEN: 'hidden',
  TEXT: 'text',
  LINK: 'link',
  IMAGE: 'image',
  MOVIE: 'movie',
};

export const featureCollection = 'FeatureCollection';

export const geojsonCustomPropertyName: GeojsonCustomPropertyName = {
  color: 'customLayerColor',
  thickness: 'customLineThickness',
  pointImageUrl: 'customPointImageUrl',
  pointImageId: 'customPointImageId',
  lineStyle: 'customLineStyle',
};

export const LINE_DASH_ARRAYS_FOR_LAYER = { solid: [1], dashed: [2, 2] };

// 管理画面側のconstants.tsxのTHICKNESS_FOR_LAYERの最小値と合わせる
export const DEFAULT_THICKNESS_FOR_LAYER = 2;

export const SORT_TYPE = {
  SPOT: 'spot',
  LAYER: 'layer',
};

export const ODS_SORT_PROPERTY = '名称_カナ';

// ▼ Form ****************************************▼
const FONT_SIZE_RATIO = 0.625;

export const FONT_SIZE = {
  // iOS Safari は input/textarea の文字サイズが 16px 未満だと
  // focus 時に自動ズームされるため、投稿フォームの基準サイズとして 16px を定義する
  formBase: '1rem',
  buttonText: `${FONT_SIZE_RATIO * 1.4}rem`,
  homeDescriptionText: `${FONT_SIZE_RATIO * 1.4}rem`,
  homeHeaderText: `${FONT_SIZE_RATIO * 2}rem`,
  inputLabel: `${FONT_SIZE_RATIO * 1.4}rem`,
  inputDescription: `${FONT_SIZE_RATIO * 1.0}rem`,
  inputText: `${FONT_SIZE_RATIO * 1.4}rem`,
  listItemText: `${FONT_SIZE_RATIO * 1.6}rem`,
  table: {
    body: `${FONT_SIZE_RATIO * 1.6}rem`,
    header: `${FONT_SIZE_RATIO * 1.4}rem`,
  },
  requiredChip: `${FONT_SIZE_RATIO * 1.2}rem`,
};

// スポットタイプ属性の入力形式
export const SPOT_TYPE_EDIT_FORMAT = {
  json: {
    formatSwitchText: 'JSONで編集',
  },
  select: {
    formatSwitchText: '戻る',
  },
};

// 投稿機能
export const UGC_POST_STATUS_MAP = {
  投稿依頼: 'draft',
  審査中: 'review',
  公開: 'approved',
  公開終了: 'closed',
  公開不可: 'rejected',
};

export const MAX_FILE_SIZE = 10485760; // 10MB

export const SPOT_DIALOG_PLACEHOLDER = {
  twoColumnKeys: ['zipcode', 'address', 'phone'],
  threeColumnKeys: ['text', 'url'],
};

export const ACTION_TYPE_MESSAGE = {
  create: '登録',
  edit: '編集',
  delete: '削除',
  commonSuccess: '正常に処理が',
  commonError: '処理が',
};

export const ACTION_TYPE_NAME = {
  create: 'create',
  edit: 'edit',
  delete: 'delete',
};

export const LAYER_ID_SPOT_MOVEMENT_TRACKS = 'spotMovementTracks';

// ▲ Form ****************************************▲

export const INITIAL_LAYER_BORDER_COLOR = 'black';

export const CUCON_API_PATH = {
  hasura: '/v1/graphql',
  appSync: '/v1/management',
  authorizationInfo: '/v1/authorization-info',
  authorizationTenantServiceInfo: '/v1/authorization-tenant-service-info',
  s3PresignedUrl: '/v1/s3-presigned-url',
  s3Delete: '/v1/s3/delete',
  s3LegacyDelete: '/v1/s3/legacy-delete',
};

export const CUCON_OIDC_API_PATH = {
  issuer: '/oidc',
};

export const DEFAULT_LAYER_POINT_IMAGE_ID = 'icon_layer-cross_R';

export const LAYER_INTERACTION_STATUS = {
  NONE: null,
  HOVER: 'hover',
  CLICK: 'click',
};

export const POST_STATUS_COLORS = {
  投稿依頼: { background: '#3cb371', text: '#ffffff' },
  審査中: { background: '#1e90ff', text: '#ffffff' },
  公開: { background: '#f2613d', text: '#ffffff' },
  公開終了: { background: '#a9a9a9', text: '#ffffff' },
  公開不可: { background: '#696969', text: '#ffffff' },
  default: { background: '#e0e0e0', text: '#000000' },
};

export const POST_PRIVATE_BORDER_COLOR = '#808080';

// NOTE: グラフの操作説明（ズームとドラッグ）をデバイスごとにまとめた定数
export const GRAPH_ZOOM_INSTRUCTION = {
  desktop: 'ズーム：マウスホイールを上下に操作',
  mobile: 'ズーム：2本の指で触れ、指の間隔を広げる・狭める',
};

export const GRAPH_DRAG_INSTRUCTION = {
  desktop: 'グラフを移動：ズーム後、グラフをドラッグ',
  mobile: 'グラフを移動：ズーム後、1本指で左右にはらうように操作',
};

export const AUTH_COOKIES = ['codeVerifier', 'accessToken', 'refreshToken'];

export const FLEXIBLE_SPOT_STATUS_FIELD_TYPE = {
  TEXT: 'text',
  RADIO: 'radio',
  SELECT: 'select',
  CHECKBOX_GROUP: 'checkbox_group',
  TOGGLE: 'toggle',
  DATE: 'date',
  DATE_RANGE: 'date_range',
} as const;
